import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError, retry } from "@reduxjs/toolkit/query/react";
import * as Sentry from "@sentry/react";

import { RootState } from "@/store/hooks";

import { handleApiError } from "./errorHandler";
import { generateRequestMetadata, logRequest, logResponse } from "./interceptors";
import { requestMonitor } from "./monitor";
import { ExtraOptions } from "./types";

const baseUrl = import.meta.env.VITE_APP_API_URL;

type BaseQuery = BaseQueryFn<FetchArgs | string, unknown, FetchBaseQueryError, ExtraOptions>;
const baseQueryWithRetry = retry<BaseQuery>(
  async (args, api, extraOptions) => {
    const version = extraOptions?.version;
    const dynamicBaseUrl = version ? `${baseUrl}/${version}` : baseUrl;

    const dynamicBaseQuery = fetchBaseQuery({
      baseUrl: dynamicBaseUrl,
      prepareHeaders: (headers, { getState }) => {
        const { auth: authState } = getState() as RootState;
        const driverToken = authState.driver?.accessToken;
        const expeditionToken = authState.expedition?.accessToken;
        const tokenFromState = driverToken || expeditionToken;

        const token = tokenFromState;
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        return headers;
      }
    });

    return dynamicBaseQuery(args, api, extraOptions);
  },
  { maxRetries: 0 }
);

export const baseQuery: BaseQuery = async (args, api, extraOptions) => {
  const metadata = generateRequestMetadata();

  logRequest(args, metadata, false);
  requestMonitor.logRequest(args, metadata.requestId, metadata.timestamp);
  const result = await baseQueryWithRetry(args, api, extraOptions);

  if (result.error) {
    const status = result.error.status;

    if (status !== 404) {
      Sentry.addBreadcrumb({
        category: "api",
        message: api.endpoint,
        data: typeof args === "string" ? { url: args } : args
      });

      Sentry.captureException(new Error("API Request Failed"), {
        extra: {
          endpoint: api.endpoint,
          args,
          error: result.error
        }
      });
    }
  }

  logResponse(result, metadata, false);
  requestMonitor.logResponse(result, metadata.requestId, metadata.timestamp);
  const handledResult = handleApiError(result.error, api, result);

  return {
    ...handledResult,
    meta: { ...handledResult.meta, ...metadata }
  };
};
